/**
 * Deal analytics — the client half of `briefs/CONTRACT.md` (endpoints under
 * `/api/v1/dashboards/analytics`, built by `services/dashboards/analytics.py`).
 *
 * Every number here is summed server-side from one `DealFact` per live deal,
 * which is resolved through the same readers the Pipeline grid uses — so a
 * figure on a board equals the grid's figure for the same deals. The client
 * never re-derives a measure; it formats what was sent and drills to the
 * `dealIds` that were sent with it.
 *
 * Wire rules: camelCase keys used verbatim, amounts in dollars, percentages as
 * 0–100 points, dates `YYYY-MM-DD`, and `null` means "no source" — never 0.
 */
import api from './api'
import type { DrillDescriptor, Figure, FigureFormat } from '@/types/dashboards'

const BASE = '/dashboards/analytics'

// ── Vocabulary ─────────────────────────────────────────────────────────────

/** 'signed' = term sheet signed, IC approved or rate locked, not yet funded.
 *  Never called "committed": on the boards that word means committed exposure
 *  (IC approved onward, funded included). */
export type Outcome = 'requested' | 'signed' | 'funded' | 'dead'

/** Pipeline order, earliest first — the order every stack and legend reads. */
export const OUTCOMES: Outcome[] = ['requested', 'signed', 'funded', 'dead']

export const OUTCOME_LABEL: Record<Outcome, string> = {
  requested: 'Requested',
  signed: 'Signed',
  funded: 'Funded',
  dead: 'Dead',
}

/** What each outcome bucket holds, for the legend tooltips. */
export const OUTCOME_DEFINITION: Record<Outcome, string> = {
  requested: 'Triage through term sheet issued — under review, not exposure',
  signed: 'Term sheet signed, IC approved or rate locked — not yet funded',
  funded: 'Stage Funded',
  dead: 'Dead or killed',
}

/** Black-gold only: the funded book is the deep brand gold, signed terms the
 *  pale champagne, requests a warm neutral and the dead a dark bronze. Colour
 *  is never the only cue — every chart carries a legend and every table a
 *  labelled column. */
export const OUTCOME_COLOR: Record<Outcome, string> = {
  requested: '#6E6A62',
  signed: '#F1D58A',
  funded: '#C49A43',
  dead: '#7A5A2A',
}

/** The neutral every "Not recorded" group is drawn in. */
export const NOT_RECORDED_COLOR = '#3A3835'

/** The board's gold ramp, ordered so neighbouring categories differ in tone
 *  (dark, light, mid, …) — categorical colour without leaving black-gold. */
const GOLD_CATEGORY_ORDER = ['#C49A43', '#F1D58A', '#8B6427', '#E5B840', '#A67C1A', '#F6DF9A', '#9D7A33', '#D9B768']

export function goldCategory(index: number): string {
  return GOLD_CATEGORY_ORDER[((index % GOLD_CATEGORY_ORDER.length) + GOLD_CATEGORY_ORDER.length) % GOLD_CATEGORY_ORDER.length]
}

export type AnalyticsDimension =
  | 'stage'
  | 'outcome'
  | 'underwriter'
  | 'secondaryUnderwriter'
  | 'responsibleParty'
  | 'originator'
  | 'brokerage'
  | 'brokerName'
  | 'sponsor'
  | 'propertyType'
  | 'state'
  | 'city'
  | 'capitalProgram'
  | 'position'
  | 'purpose'
  | 'rateType'
  | 'receivedMonth'
  | 'fundedMonth'
  | 'maturityYear'
  | 'dealSizeBand'
  | 'ltvBand'

/** Labels used until `/dimensions` answers, and for dimensions the server
 *  lists without one. The server's list is authoritative for WHICH exist. */
export const DIMENSION_LABEL: Record<AnalyticsDimension, string> = {
  stage: 'Stage',
  outcome: 'Outcome',
  underwriter: 'Underwriter',
  secondaryUnderwriter: 'Secondary underwriter',
  responsibleParty: 'Responsible party',
  originator: 'Originator',
  brokerage: 'Brokerage',
  brokerName: 'Broker',
  sponsor: 'Sponsor',
  propertyType: 'Property type',
  state: 'State',
  city: 'City',
  capitalProgram: 'Capital program',
  position: 'Position',
  purpose: 'Purpose',
  rateType: 'Rate type',
  receivedMonth: 'Received month',
  fundedMonth: 'Funded month',
  maturityYear: 'Maturity year',
  dealSizeBand: 'Deal size',
  ltvBand: 'LTV / LTC band',
}

/** Dimensions whose keys are time or an ordered band — sorted by key, drawn as
 *  columns, never re-ranked by size. */
export const ORDERED_DIMENSIONS: ReadonlySet<string> = new Set([
  'stage',
  'outcome',
  'receivedMonth',
  'fundedMonth',
  'maturityYear',
  'dealSizeBand',
  'ltvBand',
])

export type PeopleRole =
  | 'underwriter'
  | 'responsibleParty'
  | 'secondaryUnderwriter'
  | 'analyst'
  | 'originator'
  | 'broker'
  | 'brokerage'
  | 'sponsor'
  /** Deals a person works on without being Responsible Party (T-55). */
  | 'supporting'
  /** Everyone internal, in any role (T-54). */
  | 'team'

export const PEOPLE_ROLES: PeopleRole[] = [
  'underwriter',
  'responsibleParty',
  'originator',
  'brokerage',
  'broker',
  'sponsor',
]

export const ROLE_LABEL: Record<PeopleRole, string> = {
  underwriter: 'Underwriter',
  responsibleParty: 'Responsible party',
  secondaryUnderwriter: 'Secondary underwriter',
  analyst: 'Analyst',
  originator: 'Originator',
  broker: 'Broker',
  brokerage: 'Brokerage',
  sponsor: 'Sponsor',
  supporting: 'Supporting',
  team: 'Team member',
}

/** Roles that name a person on the desk (the rest are counterparties). */
export const INTERNAL_ROLES: ReadonlySet<PeopleRole> = new Set<PeopleRole>([
  'underwriter',
  'responsibleParty',
  'secondaryUnderwriter',
  'analyst',
  'originator',
  'supporting',
  'team',
])

/** The DealFact field each role reads — used to split a person's deals into
 *  the ones they hold in this role and the ones they only support (T-55). */
export const ROLE_FACT_FIELD: Partial<Record<PeopleRole, keyof DealFact>> = {
  underwriter: 'underwriter',
  responsibleParty: 'responsibleParty',
  secondaryUnderwriter: 'secondaryUnderwriter',
  analyst: 'analyst',
  originator: 'originator',
  broker: 'brokerName',
  brokerage: 'brokerage',
  sponsor: 'sponsor',
}

/** The pivot dimension a role corresponds to, for "open this person in the
 *  Explorer" and for filter chips. */
export const ROLE_DIMENSION: Partial<Record<PeopleRole, AnalyticsDimension>> = {
  underwriter: 'underwriter',
  responsibleParty: 'responsibleParty',
  secondaryUnderwriter: 'secondaryUnderwriter',
  originator: 'originator',
  broker: 'brokerName',
  brokerage: 'brokerage',
  sponsor: 'sponsor',
}

// ── Facts ──────────────────────────────────────────────────────────────────

export interface DealFact {
  id: string
  name: string | null
  stage: string | null
  stageLabel: string | null
  stageRank: number | null
  outcome: Outcome
  dealStatus: string | null
  requestedAmount: number | null
  commitmentAmount: number | null
  initialAdvance: number | null
  futureFunding: number | null
  fundedAmount: number | null
  ltvLtc: number | null
  ltvLtcBasis: 'ltv' | 'ltc' | 'ltvLtc' | null
  ltvStabilized: number | null
  dscr: number | null
  debtYield: number | null
  debtYieldStabilized: number | null
  couponPct: number | null
  /** The coupon as the record states it ("SOFR + 3.50%"), printed verbatim. */
  couponText?: string | null
  spreadBps: number | null
  rateType: string | null
  termMonths: number | null
  extensionMonths: number | null
  closingDate: string | null
  maturityDate: string | null
  /** 'closing+term' (settled rule) or 'stated'. */
  maturityBasis?: string | null
  extendedMaturityDate: string | null
  receivedDate: string | null
  statusDate: string | null
  fundedDate: string | null
  /** Received → funded in whole days, where both are dated and received first. */
  daysToFund?: number | null
  killedDate: string | null
  killReason: string | null
  killedAtStage: string | null
  daysInStatus: number | null
  daysOpen: number | null
  propertyType: string | null
  city: string | null
  state: string | null
  country: string | null
  capitalProgram: string | null
  position: string | null
  purpose: string | null
  units: number | null
  squareFeet: number | null
  underwriter: string | null
  secondaryUnderwriter: string | null
  responsibleParty: string | null
  originator: string | null
  /** Optional until the book carries one (T-57: analyst on n of 168). */
  analyst?: string | null
  brokerName: string | null
  brokerage: string | null
  sponsor: string | null
  borrower: string | null
  propertyId: string | number | null
  sponsorId: string | number | null
}

// ── Measures ───────────────────────────────────────────────────────────────

export interface Measures {
  count: number
  requested: number | null
  commitment: number | null
  funded: number | null
  activeCount: number
  signedCount: number
  fundedCount: number
  deadCount: number
  conversionPct: number | null
  /** T-71: deals reaching Post-screen Quote or beyond ÷ deals submitted.
   *  Optional — a server built before the addendum ships none. */
  quoteRatePct?: number | null
  reachedQuoteCount?: number | null
  reachedTermSheetCount?: number | null
  /** Pull-through after the quote (T-71). */
  quoteToTermSheetPct?: number | null
  termSheetToFundedPct?: number | null
  requestedCount?: number
  wtdLtvLtc: number | null
  /** "n of m". */
  ltvCoverage: string | null
  wtdCouponPct: number | null
  couponCoverage: string | null
  avgDaysToFund: number | null
  avgDaysToFundN: number | null
  avgDaysInStatus?: number | null
  avgDaysInStatusN?: number | null
  dealIds: string[]
}

export type MeasureKey =
  | 'count'
  | 'requested'
  | 'commitment'
  | 'funded'
  | 'activeCount'
  | 'signedCount'
  | 'fundedCount'
  | 'deadCount'
  | 'conversionPct'
  | 'quoteRatePct'
  | 'quoteToTermSheetPct'
  | 'termSheetToFundedPct'
  | 'wtdLtvLtc'
  | 'wtdCouponPct'
  | 'avgDaysToFund'

export interface MeasureMeta {
  key: MeasureKey
  label: string
  /** How the value prints, in the board kit's own vocabulary. */
  format: FigureFormat
  /** Days print with a 'd' suffix. */
  unit?: 'days'
  /** The outcome subset of a row's deals this measure is computed over, when
   *  it is narrower than the row. Drives which ids a click opens. */
  outcomes?: Outcome[]
  /** The coverage string or sample size printed beside an average. */
  coverage?: 'ltvCoverage' | 'couponCoverage' | 'avgDaysToFundN'
}

export const MEASURES: MeasureMeta[] = [
  { key: 'count', label: 'Deals', format: 'count' },
  { key: 'requested', label: 'Requested', format: 'currencyM' },
  { key: 'commitment', label: 'Commitment', format: 'currencyM' },
  { key: 'funded', label: 'Funded', format: 'currencyM', outcomes: ['funded'] },
  { key: 'activeCount', label: 'Open', format: 'count', outcomes: ['requested', 'signed'] },
  { key: 'signedCount', label: 'Signed #', format: 'count', outcomes: ['signed'] },
  { key: 'fundedCount', label: 'Funded #', format: 'count', outcomes: ['funded'] },
  { key: 'deadCount', label: 'Dead #', format: 'count', outcomes: ['dead'] },
  { key: 'quoteRatePct', label: 'Quote rate', format: 'pct' },
  { key: 'quoteToTermSheetPct', label: 'Quote → TS', format: 'pct' },
  { key: 'termSheetToFundedPct', label: 'TS → funded', format: 'pct' },
  { key: 'conversionPct', label: 'Funded rate', format: 'pct' },
  { key: 'wtdLtvLtc', label: 'Wtd LTV/LTC', format: 'pct', coverage: 'ltvCoverage' },
  { key: 'wtdCouponPct', label: 'Wtd coupon', format: 'pct', coverage: 'couponCoverage' },
  {
    key: 'avgDaysToFund',
    label: 'Days to fund',
    format: 'count',
    unit: 'days',
    outcomes: ['funded'],
    coverage: 'avgDaysToFundN',
  },
]

export const MEASURE_BY_KEY: Record<MeasureKey, MeasureMeta> = Object.fromEntries(
  MEASURES.map((meta) => [meta.key, meta]),
) as Record<MeasureKey, MeasureMeta>

/** The four the Explorer can chart (a sum is stackable; a rate is not). */
export type ChartMeasure = 'count' | 'requested' | 'commitment' | 'funded'
export const CHART_MEASURES: ChartMeasure[] = ['count', 'requested', 'commitment', 'funded']

// ── Endpoint shapes ────────────────────────────────────────────────────────

export interface DimensionsResponse {
  dimensions: Array<{ key: string; label: string }>
  measures: Array<{ key: string; label: string; format: string }>
}

/** `null` in a value list selects the "Not recorded" group. */
export type AnalyticsFilters = Partial<Record<string, Array<string | null>>>

export interface PivotChild {
  key: string | null
  label: string
  measures: Measures
}

export interface PivotRow {
  key: string | null
  label: string
  measures: Measures
  children?: PivotChild[]
}

export interface PivotRequest {
  dimension: string
  by?: string | null
  filters?: AnalyticsFilters
  sort?: string
  limit?: number
}

export interface PivotResponse {
  dimension: string
  by: string | null
  rows: PivotRow[]
  totals: { measures: Measures } | Measures
  asOf: string | null
  basis?: string | null
}

export interface FunnelStageRow {
  stage: string
  label: string
  count: number
  amount: number | null
  dealIds: string[]
  convToNextPct: number | null
  avgDaysInStage: number | null
  avgDaysInStageN: number | null
}

export interface FunnelResponse {
  stages: FunnelStageRow[]
  dead: {
    count: number
    amount: number | null
    dealIds: string[]
    byStage: Array<{ stage: string; count: number }>
  }
  asOf: string | null
  basis?: string | null
}

export interface FlowCell {
  count: number
  amount: number | null
  dealIds: string[]
}

export interface FlowPoint {
  month: string
  label: string
  received: FlowCell
  signed: FlowCell
  funded: FlowCell
  dead: FlowCell
}

/** 'event' (server default): each series dated by its own event — received
 *  by Date Received, signed by the day it entered its stage, funded by the
 *  funded date, dead by the kill date. The others place every deal in the
 *  month of one date and split it by outcome now (a cohort view). */
export type FlowDateField = 'event' | 'received' | 'funded' | 'status'

export interface FlowResponse {
  dateField?: FlowDateField
  months?: number
  points: FlowPoint[]
  basis: string | null
  coverage?: Partial<
    Record<'received' | 'signed' | 'funded' | 'dead', { undated: number; undatedIds: string[]; outsideWindow: number }>
  >
}

export interface PersonSummary {
  name: string
  count: number
  commitment: number | null
  funded: number | null
  fundedCount: number
  activeCount: number
  deadCount: number
  conversionPct: number | null
  quoteRatePct?: number | null
  avgDaysToFund: number | null
  lastActivityAt: string | null
  avgDaysToFundN?: number | null
  quoteToTermSheetPct?: number | null
  termSheetToFundedPct?: number | null
  requested?: number | null
  /** Every deal this person holds the role on. */
  dealIds?: string[]
  /** On the governed team roster (Settings › Reference Data). */
  onRoster?: boolean
  /** Internal roles: deals they are Responsible Party for / support (T-55). */
  responsibleCount?: number | null
  supportingCount?: number | null
  responsibleIds?: string[]
  supportingIds?: string[]
}

export interface PeopleResponse {
  role: string
  roleLabel?: string
  people: PersonSummary[]
  /** Deals naming no one in this role. */
  unattributed?: { count: number; dealIds: string[] }
  /** How the list is built (T-54) — shown as the picker's tooltip. */
  basis?: string | null
  rateLabels?: Record<string, string>
  asOf?: string | null
}

/** A relationship row on a profile: pivot measures plus the latest dated deal
 *  event and an at-risk flag that only ever comes with its reason (T-67). */
export interface RelationshipRow extends PivotRow {
  lastDealDate?: string | null
  atRisk?: boolean
  atRiskReason?: string | null
}

export interface StageSplit {
  stage: string
  count: number
  commitment: number | null
  dealIds: string[]
}

export interface PersonAging {
  triageCount: number
  triageIds: string[]
  triageAgedCount: number
  triageAgedIds: string[]
  triageOverResponseSlaCount?: number
  triageOverResponseSlaIds?: string[]
  quotesOutCount: number
  quotesOutIds: string[]
  quotesOutAgedCount: number
  quotesOutAgedIds: string[]
  quoteSlaBreachCount: number
  quoteSlaBreachIds: string[]
  daysInStatus?: Array<{ key: string; label: string; count: number; dealIds: string[] }>
  daysInStatusUnmeasured?: number
  basis?: string
}

/** Thresholds the server chose and echoes — each awaits Edward's sign-off. */
export interface AnalyticsPolicy {
  dealSizeBands?: string[]
  ltvBands?: string[]
  triageAgedDays?: number
  quoteAgedDays?: number
  responseSlaHours?: number
  quoteSlaDays?: number
  relationshipQuietDays?: number
  status?: string
}

export interface WorkloadBucket {
  count: number
  volume: number | null
  dealIds: string[]
}

export interface WorkloadPerson {
  name: string
  onRoster?: boolean
  total: { count: number; openCount: number; volume: number | null; openVolume: number | null; dealIds: string[] }
  byStage: StageSplit[]
  supporting: { count: number; byStage: StageSplit[]; dealIds: string[] }
  aging: PersonAging
}

export interface StaffingSplit {
  staffed: WorkloadBucket
  needsStaffing: WorkloadBucket
}

export interface WorkloadResponse {
  role: string
  roleLabel?: string
  stages: Array<{ stage: string; label: string }>
  people: WorkloadPerson[]
  unassigned: { count: number; byStage: StageSplit[]; dealIds: string[]; aging?: PersonAging }
  staffing: StaffingSplit & {
    byStage: Array<StaffingSplit & { stage: string }>
    byResponsibleParty: Array<StaffingSplit & { name: string; key: string | null }>
    analystCoverage?: string
    analystCoverageN?: number
    secondaryUnderwriterCoverage?: string
    secondaryUnderwriterCoverageN?: number
    basis?: string
  }
  policy?: AnalyticsPolicy
  basis?: string
  asOf?: string | null
}


export interface HeadlineMeasures {
  measures: Measures
  /** 1 = best, per measure key. */
  rank?: Partial<Record<string, number | null>>
  peerMedian?: Partial<Record<string, number | null>>
  /** Peers ranked against (optional; falls back to the people list length). */
  peers?: number
}

export interface SankeyNode {
  id: string
  label: string
  kind: 'source' | 'person' | 'outcome'
}

export interface SankeyLink {
  source: string
  target: string
  count: number
  amount: number | null
  dealIds: string[]
}

export interface ActivityItem {
  at: string | null
  dealId: string | null
  dealName: string | null
  kind: string | null
  summary: string | null
  actor: string | null
}

export interface PersonProfile {
  role: string
  roleLabel?: string
  name: string
  onRoster?: boolean
  headline: HeadlineMeasures
  funnel: FunnelResponse
  /** CONTRACT: one flow; the server ships one per date field. */
  flow: FlowResponse | Partial<Record<FlowDateField, FlowResponse>>
  mix: Partial<Record<'propertyType' | 'state' | 'capitalProgram' | 'dealSizeBand' | 'purpose', PivotRow[]>>
  relationships: Partial<Record<'brokerages' | 'brokers' | 'sponsors', RelationshipRow[]>>
  sankey: { nodes: SankeyNode[]; links: SankeyLink[]; basis?: string }
  activity: ActivityItem[]
  deals: DealFact[]
  basis?: string | null
  policy?: AnalyticsPolicy
  /** Internal roles (T-55): deals they are Responsible Party for vs support. */
  responsibleFor?: { count: number; byStage: StageSplit[]; dealIds: string[] }
  supporting?: { count: number; byStage: StageSplit[]; dealIds: string[]; basis?: string }
  aging?: PersonAging
}

/** The profile's flow for one date field, whichever shape the server sent. */
export function profileFlow(
  profile: Pick<PersonProfile, 'flow'> | null | undefined,
  field: FlowDateField,
): FlowResponse | null {
  const flow = profile?.flow as (FlowResponse & Partial<Record<FlowDateField, FlowResponse>>) | undefined
  if (!flow) return null
  if (Array.isArray(flow.points)) return flow
  return flow[field] ?? flow.event ?? flow.received ?? null
}


// ── Calls ──────────────────────────────────────────────────────────────────

export const dashboardAnalyticsService = {
  dimensions: (signal?: AbortSignal) =>
    api.get<DimensionsResponse>(`${BASE}/dimensions`, { signal }).then((r) => r.data),

  pivot: (body: PivotRequest, signal?: AbortSignal) =>
    api.post<PivotResponse>(`${BASE}/pivot`, cleanPivot(body), { signal }).then((r) => r.data),

  funnel: (filters: AnalyticsFilters = {}, signal?: AbortSignal) =>
    api.post<FunnelResponse>(`${BASE}/funnel`, { filters }, { signal }).then((r) => r.data),

  flow: (
    body: { filters?: AnalyticsFilters; dateField: FlowDateField; months?: number },
    signal?: AbortSignal,
  ) => api.post<FlowResponse>(`${BASE}/flow`, body, { signal }).then((r) => r.data),

  people: (role: PeopleRole, signal?: AbortSignal) =>
    api
      .get<PeopleResponse>(`${BASE}/people`, { params: { role }, signal })
      .then((r) => r.data),

  person: (role: PeopleRole, name: string, signal?: AbortSignal) =>
    api
      .get<PersonProfile>(`${BASE}/people/${encodeURIComponent(role)}/${encodeURIComponent(name)}`, {
        signal,
      })
      .then((r) => r.data),

  workload: (body: { role?: PeopleRole; filters?: AnalyticsFilters } = {}, signal?: AbortSignal) =>
    api
      .post<WorkloadResponse>(
        `${BASE}/workload`,
        { role: body.role ?? 'responsibleParty', filters: cleanFilters(body.filters) },
        { signal },
      )
      .then((r) => r.data),

  deals: (filters: AnalyticsFilters = {}, signal?: AbortSignal) =>
    api.post<{ deals: DealFact[] }>(`${BASE}/deals`, { filters }, { signal }).then((r) => r.data),

  dealsById: (ids: string[], signal?: AbortSignal) =>
    api
      .get<{ deals: DealFact[] }>(`${BASE}/deals`, { params: { ids: ids.join(',') }, signal })
      .then((r) => r.data),
}

/** Drop empty filter lists and an absent `by` so the cache key and the body
 *  agree on "no filter" — `{state: []}` and `{}` are the same question. */
export function cleanFilters(filters: AnalyticsFilters | undefined): AnalyticsFilters {
  const out: AnalyticsFilters = {}
  for (const [key, values] of Object.entries(filters ?? {})) {
    if (Array.isArray(values) && values.length > 0) out[key] = values
  }
  return out
}

function cleanPivot(body: PivotRequest): PivotRequest {
  const out: PivotRequest = { dimension: body.dimension, filters: cleanFilters(body.filters) }
  if (body.by) out.by = body.by
  if (body.sort) out.sort = body.sort
  if (body.limit) out.limit = body.limit
  return out
}

// ── Pure helpers (tested) ──────────────────────────────────────────────────

/** Index of the stage a deal is quoted at (Post-screen Quote) in the
 *  platform's stage order — T-71's quote rate counts deals at or past it. */
export const QUOTE_STAGE_RANK = 2
/** Term Sheet Issued — where quote → term sheet pull-through is counted. */
export const TERM_SHEET_STAGE_RANK = 3

const STAGE_RANK_BY_LABEL: Record<string, number> = {
  Triage: 0,
  'Pre-screen Indication': 1,
  'Post-screen Quote': 2,
  'Term Sheet Issued': 3,
  'Term Sheet Signed': 4,
  'IC / Committee Approved': 5,
  'Rate Lock': 6,
  Funded: 7,
}

/** Did the deal reach the stage at `rank` — at or past it now, or killed at
 *  or past it (the server's `analytics.reached`). */
export function reachedStage(
  fact: Pick<DealFact, 'outcome' | 'stageRank' | 'killedAtStage'>,
  rank: number,
): boolean {
  if (fact.outcome === 'dead') {
    const killed = fact.killedAtStage != null ? STAGE_RANK_BY_LABEL[fact.killedAtStage] : undefined
    return killed != null && killed >= rank
  }
  return fact.stageRank != null && fact.stageRank >= rank
}


/** Totals arrive as `{measures}` per CONTRACT; tolerate a bare measures object. */
export function totalsOf(response: Pick<PivotResponse, 'totals'> | null | undefined): Measures | null {
  const totals = response?.totals as { measures?: Measures } | Measures | undefined
  if (!totals) return null
  if ('measures' in totals && totals.measures) return totals.measures
  return totals as Measures
}

/** The universal drill for an exact set of deals. Null for an empty set — a
 *  number that stands on no deal must not advertise a click. */
export function drillForIds(ids: readonly string[] | null | undefined, title: string): DrillDescriptor | null {
  if (!ids || ids.length === 0) return null
  return { dataset: 'loans', filters: { ids: [...ids] }, title }
}

/** Which of a row's deals each measure is computed over, as a predicate on
 *  the deal's fact. A measure absent here stands on every deal in the row. */
export const MEASURE_SUBSET: Partial<Record<MeasureKey, (fact: DealFact) => boolean>> = {
  requested: (fact) => fact.requestedAmount != null,
  commitment: (fact) => fact.commitmentAmount != null,
  funded: (fact) => fact.outcome === 'funded',
  activeCount: (fact) => fact.outcome === 'requested' || fact.outcome === 'signed',
  signedCount: (fact) => fact.outcome === 'signed',
  fundedCount: (fact) => fact.outcome === 'funded',
  deadCount: (fact) => fact.outcome === 'dead',
  // A rate opens its numerator: the deals that were funded / quoted.
  conversionPct: (fact) => fact.outcome === 'funded',
  quoteRatePct: (fact) => reachedStage(fact, QUOTE_STAGE_RANK),
  quoteToTermSheetPct: (fact) => reachedStage(fact, TERM_SHEET_STAGE_RANK),
  termSheetToFundedPct: (fact) => fact.outcome === 'funded',
  // An average opens the deals it was averaged over.
  wtdLtvLtc: (fact) => fact.ltvLtc != null && fact.commitmentAmount != null,
  wtdCouponPct: (fact) => fact.couponPct != null && fact.commitmentAmount != null,
  avgDaysToFund: (fact) => fact.outcome === 'funded' && fact.daysToFund != null,
}

/** The ids a measure on a row stands on. With the facts at hand the subset is
 *  exact (the deals with an LTV, the funded deals, the quoted deals). Without
 *  them, an outcome measure reads its outcome children when the pivot was
 *  asked `by: 'outcome'`; anything else opens the row's whole set. */
export function idsForMeasure(
  row: Pick<PivotRow, 'measures' | 'children'>,
  measure: MeasureKey,
  by: string | null | undefined,
  facts?: ReadonlyMap<string, DealFact>,
): string[] {
  const all = row.measures.dealIds ?? []
  const predicate = MEASURE_SUBSET[measure]
  if (!predicate) return all
  if (facts && facts.size > 0) {
    return all.filter((id) => {
      const fact = facts.get(id)
      return fact ? predicate(fact) : false
    })
  }
  const meta = MEASURE_BY_KEY[measure]
  if (meta?.outcomes && by === 'outcome' && row.children) {
    const wanted = new Set<string>(meta.outcomes)
    return row.children
      .filter((child) => child.key != null && wanted.has(child.key))
      .flatMap((child) => child.measures.dealIds ?? [])
  }
  return all
}

/** One measure as a board Figure, carrying its drill. */
export function measureFigure(
  measures: Measures | null | undefined,
  measure: MeasureKey,
  options: { ids?: string[]; title?: string; label?: string } = {},
): Figure {
  const meta = MEASURE_BY_KEY[measure]
  const raw = measures ? (measures as unknown as Record<string, unknown>)[measure] : null
  const value = typeof raw === 'number' && Number.isFinite(raw) ? raw : null
  const coverage = meta.coverage && measures ? (measures as unknown as Record<string, unknown>)[meta.coverage] : null
  const ids = options.ids ?? measures?.dealIds ?? []
  // A count of zero is a measured zero; only a sum or rate can be "no source".
  const drill = value != null && value !== 0 ? drillForIds(ids, options.title ?? meta.label) : null
  return {
    label: options.label ?? meta.label,
    value,
    format: meta.format,
    ...(drill ? { drill } : {}),
    ...(coverage != null && coverage !== ''
      ? { basis: meta.coverage === 'avgDaysToFundN' ? `n = ${coverage}` : `on ${coverage}` }
      : {}),
  }
}

/** "12d" for a day measure, the kit's format otherwise — used where a value
 *  prints outside FigureValue (chart ticks, tooltips). */
export function formatMeasure(value: number | null | undefined, measure: MeasureKey): string {
  if (value == null || !Number.isFinite(value)) return '--'
  const meta = MEASURE_BY_KEY[measure]
  switch (meta.format) {
    case 'currencyM':
    case 'currency':
      return compactCurrency(value)
    case 'pct':
      return `${value.toFixed(1)}%`
    default:
      return meta.unit === 'days'
        ? `${Math.round(value).toLocaleString('en-US')}d`
        : Math.round(value).toLocaleString('en-US')
  }
}

export function compactCurrency(amount: number | null | undefined): string {
  if (amount == null || !Number.isFinite(amount)) return '--'
  const sign = amount < 0 ? '-' : ''
  const abs = Math.abs(amount)
  if (abs >= 1_000_000_000) return `${sign}$${(abs / 1_000_000_000).toFixed(2)}B`
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1)}M`
  if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(0)}K`
  return `${sign}$${abs.toFixed(0)}`
}

/** What a deal that came without a brokerage reads as (the server's own label). */
export const NO_BROKERAGE_LABEL = 'Direct / no broker'

/** A group's label. The wire's `null` key is the no-value group; its label is
 *  the server's ("Unassigned", "Direct / no broker"), else "Not recorded". */
export function groupLabel(key: string | null, label?: string | null): string {
  if (key == null) return label?.trim() || 'Not recorded'
  return label?.trim() || key
}

/** Sort rows by a measure, nulls last, ties by label — or, for an ordered
 *  dimension (months, bands, stages), keep the server's order. */
export function sortRows<T extends { key: string | null; label: string; measures: Measures }>(
  rows: readonly T[],
  measure: MeasureKey | 'label',
  dir: 'asc' | 'desc',
): T[] {
  const sign = dir === 'asc' ? 1 : -1
  return [...rows].sort((a, b) => {
    // The no-value group ("Not recorded", "Direct / no broker", "Unassigned")
    // is never ranked among the named ones: it sits last in any sort.
    if ((a.key == null) !== (b.key == null)) return a.key == null ? 1 : -1
    if (measure === 'label') return sign * a.label.localeCompare(b.label)
    const av = (a.measures as unknown as Record<string, unknown>)[measure]
    const bv = (b.measures as unknown as Record<string, unknown>)[measure]
    const an = typeof av === 'number' ? av : null
    const bn = typeof bv === 'number' ? bv : null
    if (an == null && bn == null) return a.label.localeCompare(b.label)
    if (an == null) return 1
    if (bn == null) return -1
    if (an === bn) return a.label.localeCompare(b.label)
    return sign * (an - bn)
  })
}

/** A plain-language statement of what a filter set selects — "Underwriter:
 *  Andrew Fawer · Outcome: Funded" — for every tile's population line (T-51). */
export function describeFilters(
  filters: AnalyticsFilters,
  labelFor: (dimension: string) => string = (d) => DIMENSION_LABEL[d as AnalyticsDimension] ?? d,
): string {
  const parts = Object.entries(cleanFilters(filters)).map(([dimension, values]) => {
    const shown = (values ?? []).map((value) =>
      value == null
        ? 'Not recorded'
        : dimension === 'outcome' && value in OUTCOME_LABEL
          ? OUTCOME_LABEL[value as Outcome]
          : value,
    )
    return `${labelFor(dimension)}: ${shown.join(', ')}`
  })
  return parts.length ? parts.join(' · ') : 'Whole live pipeline'
}

/** Add one value to a filter set (or remove it when already present). */
export function toggleFilterValue(
  filters: AnalyticsFilters,
  dimension: string,
  value: string | null,
): AnalyticsFilters {
  const current = filters[dimension] ?? []
  const has = current.some((entry) => entry === value)
  const next = has ? current.filter((entry) => entry !== value) : [...current, value]
  return cleanFilters({ ...filters, [dimension]: next })
}

export function removeFilter(filters: AnalyticsFilters, dimension: string): AnalyticsFilters {
  const next = { ...filters }
  delete next[dimension]
  return cleanFilters(next)
}

/** URL form of a filter set: compact JSON, or '' for none. */
export function encodeFilters(filters: AnalyticsFilters): string {
  const clean = cleanFilters(filters)
  return Object.keys(clean).length ? JSON.stringify(clean) : ''
}

/** Read a filter set back from the URL. Anything malformed reads as "no
 *  filter" rather than throwing — a hand-edited link must not crash the board. */
export function decodeFilters(raw: string | null | undefined): AnalyticsFilters {
  if (!raw) return {}
  try {
    const parsed = JSON.parse(raw) as unknown
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {}
    const out: AnalyticsFilters = {}
    for (const [key, values] of Object.entries(parsed as Record<string, unknown>)) {
      if (!Array.isArray(values)) continue
      const kept = values.filter((v): v is string | null => v === null || typeof v === 'string')
      if (kept.length) out[key] = kept
    }
    return out
  } catch {
    return {}
  }
}

/** Cumulative running total of a numeric series; a null period carries the
 *  running total forward rather than resetting it or inventing a value. */
export function cumulative(values: ReadonlyArray<number | null>): Array<number | null> {
  let running: number | null = null
  return values.map((value) => {
    if (value != null) running = (running ?? 0) + value
    return running
  })
}

/** Median of the present values, or null when there are none. */
export function median(values: ReadonlyArray<number | null | undefined>): number | null {
  const present = values.filter((v): v is number => typeof v === 'number' && Number.isFinite(v))
  if (present.length === 0) return null
  const sorted = [...present].sort((a, b) => a - b)
  const mid = Math.floor(sorted.length / 2)
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2
}

/** Quote rate over a set of facts, for when the server ships no
 *  `quoteRatePct`: deals whose stage is Post-screen Quote or later (funded
 *  included) ÷ deals submitted. */
export function quoteRateOf(facts: ReadonlyArray<Pick<DealFact, 'stageRank'>>): {
  pct: number | null
  quoted: number
  submitted: number
} {
  const submitted = facts.length
  const quoted = facts.filter((fact) => (fact.stageRank ?? -1) >= QUOTE_STAGE_RANK).length
  return { pct: submitted ? (quoted / submitted) * 100 : null, quoted, submitted }
}
